// Category Bar Component
import { I18nManager } from "../../services/i18n-manager";
import { ProductService } from "../../services/product-service";

export interface Category {
  id: string;
  name: Record<string, string>;
  slug: string;
  image_url: string;
  sort_order: number;
}

export class CategoryBar {
  private productService: ProductService;
  private i18nManager: I18nManager;
  private categories: Category[] = [];
  private activeCategory: string = "all";

  constructor(productService: ProductService, i18nManager: I18nManager) {
    this.productService = productService;
    this.i18nManager = i18nManager;
  }

  init(): void {
    // Update title immediately on init to ensure it's translated
    this.updateTitle();
    this.setupEventListeners();
    this.loadCategories();
  }

  private setupEventListeners(): void {
    // Listen for language changes
    this.i18nManager.onLanguageChange(() => {
      this.updateTitle();
      this.render();
    });

    // Listen for category clicks
    document.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      const categoryButton = target.closest(".category-button");

      if (categoryButton) {
        e.preventDefault();
        const categorySlug = categoryButton.getAttribute("data-category");
        if (categorySlug) {
          this.selectCategory(categorySlug);
        }
      }
    });
  }

  async loadCategories(): Promise<void> {
    try {
      this.categories = await this.productService.loadCategories();
      this.updateTitle();
      this.render();
    } catch (error) {
      console.error("Failed to load categories:", error);
      this.showError();
    }
  }

  private updateTitle(): void {
    const titleElement = document.getElementById("category-section-title");
    if (titleElement) {
      titleElement.textContent = this.i18nManager.t("categories.title");
    }
  }

  private render(): void {
    const categoryBar = document.getElementById("category-bar");
    if (!categoryBar) return;

    // Create "All" button
    const allButton = this.createCategoryButton(
      "all",
      this.i18nManager.t("categories.all"),
      true
    );

    // Create category buttons
    const categoryButtons = this.categories
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((category) => {
        const name = this.productService.getLocalizedName(category);
        return this.createCategoryButton(category.slug, name, false);
      });

    categoryBar.innerHTML = [allButton, ...categoryButtons].join("");
  }

  private createCategoryButton(
    slug: string,
    name: string,
    isActive: boolean
  ): string {
    const activeClass = isActive ? "active" : "";
    return `
      <button class="category-button ${activeClass}" data-category="${slug}">
        <span class="category-name">${name}</span>
      </button>
    `;
  }

  private selectCategory(slug: string): void {
    this.activeCategory = slug;
    this.updateActiveButton();
    this.notifyCategoryChange(slug);
  }

  private updateActiveButton(): void {
    const buttons = document.querySelectorAll(".category-button");
    buttons.forEach((button) => {
      button.classList.remove("active");
      if (button.getAttribute("data-category") === this.activeCategory) {
        button.classList.add("active");
      }
    });
  }

  private notifyCategoryChange(slug: string): void {
    // Dispatch custom event for other components to listen
    const event = new CustomEvent("category-changed", {
      detail: { categorySlug: slug },
    });
    document.dispatchEvent(event);
  }

  private showError(): void {
    const categoryBar = document.getElementById("category-bar");
    if (!categoryBar) return;

    categoryBar.innerHTML = `
      <div class="category-error">
        <p>${this.i18nManager.t("error.load_categories")}</p>
        <button class="glass-button" onclick="location.reload()">
          ${this.i18nManager.t("common.retry")}
        </button>
      </div>
    `;
  }

  // Public methods
  public getActiveCategory(): string {
    return this.activeCategory;
  }

  public setActiveCategory(slug: string): void {
    this.selectCategory(slug);
  }

  public getCategories(): Category[] {
    return this.categories;
  }

  public getCategoryBySlug(slug: string): Category | null {
    return this.categories.find((cat) => cat.slug === slug) || null;
  }
}
